import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useBranch } from '../contexts/BranchContext';
import { getHighAccuracyPosition, isGeolocationAvailable, formatAccuracy } from '../utils/geolocation';

const BranchesTab = ({ token, API_URL, companyProfile, showToast: parentShowToast }) => {
    const { t } = useLanguage();
    const { loadBranches: refreshGlobalBranches } = useBranch();
    const [branchesList, setBranchesList] = useState([]);
    const [isBranchesLoading, setIsBranchesLoading] = useState(false);
    
    const branchLimit = companyProfile?.branch_limit || 1;
    const isTrial = !companyProfile?.has_paid && companyProfile?.trial_ends_at && new Date(companyProfile.trial_ends_at) > new Date();
    const activeBranchesCount = branchesList.filter(b => b.is_active).length;
    const isLimitReached = !isTrial && activeBranchesCount >= branchLimit;
    const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [currentBranch, setCurrentBranch] = useState({
        name: '',
        address: '',
        phone: '',
        manager_name: '',
        manager_email: '',
        city: '',
        state: '',
        pincode: '',
        is_active: true,
        qr_latitude: null,
        qr_longitude: null,
        master_qr_location_restriction_enabled: false,
        master_qr_radius_meters: 200
    });
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: () => { } });

    const toastTimeoutRef = React.useRef(null);

    const showToast = (msg, type = 'success') => {
        if (parentShowToast) {
            parentShowToast(msg, type);
            return;
        }
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
        setToast({ show: true, message: msg, type });
        const duration = type === 'error' ? 10000 : 3000;
        toastTimeoutRef.current = setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
            toastTimeoutRef.current = null;
        }, duration);
    };

    const openConfirm = (message, onYes, options = {}) => {
        setConfirmModal({
            show: true,
            message,
            onConfirm: onYes,
            title: options.title || t('confirmDeletion'),
            confirmText: options.confirmText || t('delete'),
            confirmColor: options.confirmColor || 'bg-[#dc2626] text-white hover:bg-[#b91c1c] shadow-rose-200',
            icon: options.icon || 'fa-trash-alt text-rose-500 bg-rose-50'
        });
    };

    const loadBranches = useCallback(async () => {
        setIsBranchesLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/branches`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.success) {
                setBranchesList(json.data);
                localStorage.setItem('redsorm_cached_branches', JSON.stringify(json.data));
            }
        } catch (err) {
            const cached = localStorage.getItem('redsorm_cached_branches');
            if (cached) {
                setBranchesList(JSON.parse(cached));
                showToast(t('loadedBranchesOffline') || 'Loaded branches from offline cache', 'info');
            } else {
                showToast(t('failedLoadBranches'), 'error');
            }
        } finally {
            setIsBranchesLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [API_URL, token, t]);

    useEffect(() => {
        if (token) loadBranches();
        
        // Listen for refresh events from parent
        const handleRefreshEvent = () => {
            loadBranches();
        };

        const handleOpenAddBranch = () => {
            if (isLimitReached) {
                const isPremium = companyProfile?.subscription_plan?.toLowerCase() === 'premium';
                if (isPremium) {
                    openConfirm(
                        `You have reached your limit of ${branchLimit} branches. Would you like to buy an extra branch/outlet for your Premium plan?`,
                        () => {
                            setConfirmModal({ show: false, message: '', onConfirm: () => {} });
                            window.dispatchEvent(new CustomEvent('openBuyBranchesModal'));
                        },
                        {
                            title: 'Add Extra Branch',
                            confirmText: 'Buy Outlet',
                            confirmColor: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-200',
                            icon: 'fa-store text-emerald-600 bg-emerald-50'
                        }
                    );
                } else {
                    window.dispatchEvent(new CustomEvent('openUpgradeModal', { detail: { plan: 'premium' } }));
                }
                return;
            }
            setCurrentBranch({ name: '', address: '', phone: '', manager_name: '', manager_email: '', city: '', state: '', pincode: '', is_active: true, qr_latitude: null, qr_longitude: null, master_qr_location_restriction_enabled: false, master_qr_radius_meters: 200 });
            setIsBranchModalOpen(true);
        };
        
        window.addEventListener('refreshBranches', handleRefreshEvent);
        window.addEventListener('openAddBranchModal', handleOpenAddBranch);
        
        return () => {
            window.removeEventListener('refreshBranches', handleRefreshEvent);
            window.removeEventListener('openAddBranchModal', handleOpenAddBranch);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, loadBranches, isLimitReached, branchLimit]);

    const handleDetectLocation = async () => {
        if (!isGeolocationAvailable()) {
            showToast('Geolocation is not supported by your browser', 'error');
            return;
        }

        setIsLocating(true);
        showToast('Getting an accurate GPS fix…', 'info');

        let position;
        try {
            // A single quick reading is often a Wi-Fi/IP estimate hundreds of metres
            // off — wait for a precise GPS fix before storing branch coordinates.
            position = await getHighAccuracyPosition();
        } catch (err) {
            console.error('Geolocation error:', err);
            showToast(
                err && (err.code === 'PERMISSION_DENIED' || err.code === 'UNSUPPORTED')
                    ? 'Location permission is required. Please allow location access.'
                    : 'Unable to detect an accurate location. Please turn on GPS, move near a window and try again.',
                'warning'
            );
            setIsLocating(false);
            return;
        }

        const lat = position.latitude;
        const lng = position.longitude;

        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
            const json = await res.json();
            if (json && json.address) {
                const cityName = json.address.city || json.address.town || json.address.village || json.address.county || '';
                const stateName = json.address.state || '';
                const postcode = json.address.postcode || '';
                const addressText = json.display_name || '';

                setCurrentBranch(prev => ({
                    ...prev,
                    address: addressText,
                    city: cityName,
                    state: stateName,
                    pincode: postcode,
                    qr_latitude: lat,
                    qr_longitude: lng
                }));
                showToast(`Location details autofilled (±${formatAccuracy(position.accuracy)})!`, 'success');
            } else {
                setCurrentBranch(prev => ({
                    ...prev,
                    qr_latitude: lat,
                    qr_longitude: lng
                }));
                showToast(`Coordinates saved (±${formatAccuracy(position.accuracy)}), but address details could not be resolved`, 'warning');
            }
        } catch (err) {
            console.error('Reverse geocoding failed:', err);
            setCurrentBranch(prev => ({
                ...prev,
                qr_latitude: lat,
                qr_longitude: lng
            }));
            showToast(`Coordinates saved (±${formatAccuracy(position.accuracy)}), but address lookup failed`, 'warning');
        } finally {
            setIsLocating(false);
        }
    };

    const saveBranch = async (e) => {
        e.preventDefault();
        try {
            const method = currentBranch.id ? 'PUT' : 'POST';
            const url = currentBranch.id
                ? `${API_URL}/api/branches/${currentBranch.id}`
                : `${API_URL}/api/branches`;

            // Unchecked override = "inherit the company-level restriction". Sending
            // `null` (instead of `0`) lets the backend fall back to the company
            // setting, which is what the checkbox label promises.
            const payload = { ...currentBranch };
            if (!payload.master_qr_location_restriction_enabled) {
                payload.master_qr_location_restriction_enabled = null;
                payload.master_qr_radius_meters = null;
            }

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const json = await res.json();
            if (json.success) {
                showToast(t('branchSavedSuccess'));
                setIsBranchModalOpen(false);
                loadBranches();
                // Refresh global branches state so it appears in AdminPage dropdown
                if (refreshGlobalBranches && token) {
                    refreshGlobalBranches(token);
                }
                // Also trigger the window event for AdminPage
                window.dispatchEvent(new CustomEvent('refreshBranches'));
            } else {
                showToast(json.message || t('failedSaveBranch'), 'error');
            }
        } catch (err) {
            showToast(t('errorSavingBranch'), 'error');
        }
    };

    const handleDeleteBranch = async (id) => {
        openConfirm(t('deleteBranchConfirm'), async () => {
            try {
                const res = await fetch(`${API_URL}/api/branches/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const json = await res.json();
                if (json.success) {
                    showToast(t('branchDeletedSuccess'));
                    loadBranches();
                    // Refresh global branches state
                    if (refreshGlobalBranches && token) {
                        refreshGlobalBranches(token);
                    }
                    // Also trigger the window event for AdminPage
                    window.dispatchEvent(new CustomEvent('refreshBranches'));
                } else {
                    showToast(json.message || t('failedDeleteBranch'), 'error');
                }
            } catch (err) {
                showToast(t('errorDeletingBranch'), 'error');
            }
            setConfirmModal({ show: false, message: '', onConfirm: () => { } });
        });
    };

    return (
        <div className="fade space-y-6">
            {/* Grid */}
            {isBranchesLoading ? (
                <div className="text-center py-12"><div className="animate-spin inline-block w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full"></div></div>
            ) : branchesList.length === 0 ? (
                <div className="text-center py-16 bg-emerald-50/20 rounded-3xl border-2 border-dashed border-emerald-100 font-normal">
                    <i className="fas fa-store-slash text-emerald-200 text-5xl mb-4 block"></i>
                    <p className="text-emerald-800 font-bold text-sm">{t('noBranchesYet') || 'No branches yet'}</p>
                </div>
            ) : (
                <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                    {branchesList.map((branch) => (
                        <div
                            key={branch.id}
                            className="group bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(5,150,105,0.12)] hover:-translate-y-1.5 transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col relative"
                        >
                            {/* Card Top: Identity & Status */}
                            <div className="p-5 flex-grow">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center text-emerald-600 shadow-inner group-hover:scale-110 transition-transform duration-300">
                                        <i className="fas fa-store text-lg"></i>
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${branch.is_active
                                        ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                        : 'bg-rose-50 text-rose-600 border-rose-100'
                                        }`}>
                                        {branch.is_active ? t('active') : t('inactive')}
                                    </span>
                                </div>
                                <h3 className="text-base font-black text-gray-900 tracking-tight leading-snug mb-2 group-hover:text-emerald-700 transition-colors">{branch.name}</h3>
                                
                                {/* Manager Display */}
                                <div className="space-y-1.5 mt-3">
                                    {branch.manager_name && branch.manager_name !== 'No Manager' ? (
                                        <div className="flex items-center text-gray-600 text-xs font-semibold">
                                            <i className="fas fa-user-tie mr-2 text-emerald-600"></i>
                                            <span className="truncate">{branch.manager_name}</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center text-gray-400 text-xs font-medium">
                                            <i className="fas fa-user-slash mr-2 text-gray-300"></i>
                                            <span className="truncate">No Manager</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Card Middle: Stats Strip */}
                            <div className="px-5 py-3.5 grid grid-cols-3 gap-2 bg-slate-50/70 border-y border-gray-100">
                                {[
                                    { v: branch.stats?.menu_items || 0, l: 'menu', c: 'emerald' },
                                    { v: branch.stats?.orders || 0, l: 'orders', c: 'teal' },
                                    { v: branch.stats?.staff_count || 0, l: 'staff', c: 'blue' }
                                ].map((s, idx) => (
                                    <div key={idx} className="flex flex-col items-center">
                                        <span className={`text-sm font-black text-${s.c}-600 leading-none`}>{s.v}</span>
                                        <span className="text-[9px] font-bold uppercase text-gray-400 tracking-wider mt-1">{t(s.l)}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Card Bottom: Contact & Actions */}
                            <div className="p-5 space-y-3 bg-white">
                                <div className="flex items-start gap-2">
                                    <i className="fas fa-map-marker-alt mt-0.5 text-emerald-500 text-xs"></i>
                                    <p className="text-xs font-semibold text-gray-500 leading-snug line-clamp-1">{branch.address || 'No address added'}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-3 pt-2">
                                    <button
                                        onClick={() => { setCurrentBranch(branch); setIsBranchModalOpen(true); }}
                                        className="bg-gray-900 text-white py-2 rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-emerald-600 transition-all active:scale-95 shadow-sm"
                                    >
                                        {t('edit')}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteBranch(branch.id)}
                                        className="bg-rose-50 text-rose-600 py-2 rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-rose-600 hover:text-white transition-all active:scale-95 border border-rose-100 shadow-sm"
                                    >
                                        {t('delete')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {isBranchModalOpen && createPortal(
                <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setIsBranchModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">{currentBranch.id ? t('editBranch') : t('addNewBranch')}</h3>
                            </div>
                            <button onClick={() => setIsBranchModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition"><i className="fas fa-times text-xl"></i></button>
                        </div>
                        <form onSubmit={saveBranch} className="p-6 space-y-4">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('branchName')} <span className="text-red-500">*</span></label>
                                    <input className="input-field w-full" placeholder={t('enterBranchName') || "Enter Branch Name"} value={currentBranch.name} onChange={e => setCurrentBranch({ ...currentBranch, name: e.target.value })} required />
                                </div>
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-sm font-medium text-gray-700">{t('address')}</label>
                                        <button
                                            type="button"
                                            onClick={handleDetectLocation}
                                            disabled={isLocating}
                                            className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition disabled:opacity-50"
                                        >
                                            {isLocating ? (
                                                <>
                                                    <i className="fas fa-spinner animate-spin"></i> Locating...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-map-marker-alt"></i> Use Current Location
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <input className="input-field w-full" placeholder={t('enterAddress') || "Enter Address"} value={currentBranch.address || ''} onChange={e => setCurrentBranch({ ...currentBranch, address: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('phoneNumber')}</label>
                                        <input className="input-field w-full" placeholder={t('phoneNumberPlaceholder') || "Enter Phone Number"} value={currentBranch.phone || ''} onChange={e => setCurrentBranch({ ...currentBranch, phone: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('managerName')}</label>
                                        <input className="input-field w-full" placeholder={t('managerNamePlaceholder') || "Enter Manager Name"} value={currentBranch.manager_name || ''} onChange={e => setCurrentBranch({ ...currentBranch, manager_name: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Manager Email</label>
                                    <input type="email" className="input-field w-full" placeholder="Enter Manager Email" value={currentBranch.manager_email || ''} onChange={e => setCurrentBranch({ ...currentBranch, manager_email: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                        <input className="input-field w-full" placeholder="City" value={currentBranch.city || ''} onChange={e => setCurrentBranch({ ...currentBranch, city: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                        <input className="input-field w-full" placeholder="State" value={currentBranch.state || ''} onChange={e => setCurrentBranch({ ...currentBranch, state: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                                        <input className="input-field w-full" placeholder="Pincode" value={currentBranch.pincode || ''} onChange={e => setCurrentBranch({ ...currentBranch, pincode: e.target.value })} />
                                    </div>
                                </div>
                                <label className="flex items-center gap-3 p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100 cursor-pointer group hover:bg-emerald-50 transition-colors">
                                    <input type="checkbox" className="w-5 h-5 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300" checked={currentBranch.is_active} onChange={e => setCurrentBranch({ ...currentBranch, is_active: e.target.checked })} />
                                    <span className="font-bold text-emerald-800 text-sm">{t('branchActiveDesc')}</span>
                                </label>

                                {/* Branch Location Restriction */}
                                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div>
                                            <h4 className="font-bold text-gray-800 text-sm">Branch Location Restriction</h4>
                                            <p className="text-xs text-gray-500 mt-0.5">Override the company-level radius for this specific branch. Use "Use Current Location" above to set coordinates.</p>
                                        </div>
                                        <i className="fas fa-location-crosshairs text-blue-500 text-base mt-0.5"></i>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <label className="flex items-center gap-2 rounded-lg bg-white border border-gray-200 p-3 cursor-pointer col-span-2">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 accent-blue-600"
                                                checked={!!currentBranch.master_qr_location_restriction_enabled}
                                                onChange={e => setCurrentBranch(prev => ({ ...prev, master_qr_location_restriction_enabled: e.target.checked }))}
                                            />
                                            <span>
                                                <span className="block text-sm font-semibold text-gray-800">Enable for this branch</span>
                                                <span className="block text-xs text-gray-500">When ticked, this branch uses its own radius. When unticked, the company-level setting applies.</span>
                                            </span>
                                        </label>
                                        <div className="col-span-2">
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Branch radius (meters)</label>
                                            <input
                                                type="number" min="1" max="5000"
                                                value={currentBranch.master_qr_radius_meters || 200}
                                                onChange={e => setCurrentBranch(prev => ({ ...prev, master_qr_radius_meters: e.target.value }))}
                                                className="input-field w-full"
                                                disabled={!currentBranch.master_qr_location_restriction_enabled}
                                            />
                                        </div>
                                    </div>
                                    <div className="p-2.5 bg-white rounded-lg border border-gray-200 flex items-center gap-2">
                                        <i className={`fas fa-map-pin text-xs ${currentBranch.qr_latitude && currentBranch.qr_longitude ? 'text-blue-500' : 'text-gray-300'}`}></i>
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">Coordinates: </span>
                                            {currentBranch.qr_latitude && currentBranch.qr_longitude
                                                ? `${Number(currentBranch.qr_latitude).toFixed(6)}, ${Number(currentBranch.qr_longitude).toFixed(6)}`
                                                : <span className="text-amber-600">Not set — click "Use Current Location" above</span>}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
                                <button type="button" onClick={() => setIsBranchModalOpen(false)} className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition btn-secondary">{t('cancel')}</button>
                                <button type="submit" className="px-6 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-bold shadow-lg transition transform hover:-translate-y-0.5 btn-primary">{currentBranch.id ? t('update') : t('create')}</button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* Utility Overlays */}
            {!parentShowToast && toast.show && (
                <div className="fixed bottom-6 right-6 z-[100] bg-gray-900/95 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-fade-in border border-gray-800">
                    <i className={`fas ${toast.type === 'success' ? 'fa-check-circle text-emerald-400' : 'fa-times-circle text-rose-400'} text-lg`}></i>
                    <span className="font-bold text-xs tracking-tight">{toast.message}</span>
                </div>
            )}

            {confirmModal.show && createPortal(
                <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4" onClick={() => setConfirmModal({ show: false, message: '', onConfirm: () => { } })}>
                    <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl modal-content border border-gray-100" onClick={(e) => e.stopPropagation()}>
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl shadow-inner ${confirmModal.icon.split(' ').slice(1).join(' ')}`}>
                            <i className={`fas ${confirmModal.icon.split(' ')[0]}`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-1">{confirmModal.title}</h3>
                        <p className="text-gray-400 font-medium text-[10px] uppercase tracking-wider mb-6 leading-relaxed">{confirmModal.message}</p>
                        <div className="flex gap-3">
                            <button onClick={() => setConfirmModal({ show: false, message: '', onConfirm: () => { } })} className="flex-1 font-bold text-gray-400 hover:text-gray-600 transition-colors uppercase text-[10px] tracking-widest">{t('cancel')}</button>
                            <button onClick={confirmModal.onConfirm} className={`flex-[2] py-3.5 rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg transition-all ${confirmModal.confirmColor}`}>{confirmModal.confirmText}</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default BranchesTab;
